import {
  AVATAR_CATALOG,
  catalogItem,
  type AvatarGender,
  type AvatarSlot,
} from "./catalog";

export const AVATAR_CONFIG_VERSION = 1 as const;

export interface AvatarConfig {
  readonly version: typeof AVATAR_CONFIG_VERSION;
  readonly gender: AvatarGender;
  readonly face: string;
  readonly skin: string;
  readonly eyes: string;
  readonly brows: string;
  readonly nose: string;
  readonly mouth: string;
  readonly hair: string;
  readonly hairColor: string;
  readonly facialDetail: string;
  readonly outfit: string;
  readonly shoes: string;
  readonly accessory: string;
  readonly idle: string;
  /** Opaque server-issued id for a user-uploaded R2 portrait. */
  readonly customPortraitId?: string | undefined;
}

export const DEFAULT_AVATAR_CONFIG: AvatarConfig = {
  version: AVATAR_CONFIG_VERSION,
  gender: "woman",
  face: AVATAR_CATALOG.woman.face[0].key,
  skin: "skin-warm-2",
  eyes: "eyes-clear",
  brows: "brows-natural",
  nose: "nose-soft",
  mouth: "mouth-smile",
  hair: AVATAR_CATALOG.woman.hair[0].key,
  hairColor: "#2b1b17",
  facialDetail: "detail-soft-blush",
  outfit: AVATAR_CATALOG.woman.outfit[0].key,
  shoes: "shoes-modern-loafer",
  accessory: AVATAR_CATALOG.woman.accessory[0].key,
  idle: AVATAR_CATALOG.woman.idle[0].key,
};

export function avatarForGender(gender: AvatarGender): AvatarConfig {
  const catalog = AVATAR_CATALOG[gender];
  return {
    ...DEFAULT_AVATAR_CONFIG,
    gender,
    face: catalog.face[0].key,
    hair: catalog.hair[0].key,
    outfit: catalog.outfit[0].key,
    accessory: catalog.accessory[0].key,
    idle: catalog.idle[0].key,
    skin: gender === "woman" ? "skin-warm-2" : "skin-warm-3",
  };
}

export function updateAvatarSlot(
  config: AvatarConfig,
  slot: AvatarSlot,
  key: string,
): AvatarConfig {
  catalogItem(config.gender, slot, key);
  return { ...config, [slot]: key };
}

export function validateAvatarConfig(config: AvatarConfig): AvatarConfig {
  for (const slot of ["face", "hair", "outfit", "accessory", "idle"] as const)
    catalogItem(config.gender, slot, config[slot]);
  if (
    config.customPortraitId !== undefined &&
    !/^[0-9a-f]{8}-[0-9a-f-]{27}$/iu.test(config.customPortraitId)
  ) {
    throw new Error("Invalid custom portrait id");
  }
  return config;
}
